import { defineStore } from 'pinia';

import type {
  AggregatedBundlePriceDto,
  ListingProduct,
  AggregatedPaymentDto,
  VariantDto,
  ColorDto,
} from '~/models/ListingProduct.api.model';

type SlimmedProduct = Omit<
  ListingProduct,
  'Variants' | 'Specifications' | 'ShortDescription' | 'Seo'
>;

export type ProductCard = {
  key: string;
  product: SlimmedProduct;
  bundlePrice?: AggregatedBundlePriceDto;
  variantDefaultBundlePriceId?: number;
  payment: AggregatedPaymentDto;
  variant: VariantDto;
  colors: ColorDto[];
};

type ConditionsInUnwrappedProductsMap = { [key: string]: boolean };

type ProductStoreState = {
  unwrappedProducts: ProductCard[];
};

export const useProductStore = defineStore('productStore', {
  state: (): ProductStoreState => ({
    unwrappedProducts: [],
  }),
  actions: {
    setProducts(items: ListingProduct[] = []): void {
      if (items.length > 0) {
        const unwrappedProducts = mapListingProductsToProductCardModel(items);
        this.unwrappedProducts = unwrappedProducts;
      }
    },
  },
  getters: {
    conditionsInUnwrappedProductsMap(
      state: ProductStoreState,
    ): ConditionsInUnwrappedProductsMap {
      const newMap: ConditionsInUnwrappedProductsMap = {};

      for (const product of state.unwrappedProducts) {
        const condition = product?.variant?.Condition?.toLocaleLowerCase();

        if (!newMap[condition] && !!condition) {
          newMap[condition] = true;
        }
      }

      return newMap;
    },
  },
});

export const mapListingProductsToProductCardModel = (
  products: ListingProduct[],
): ProductCard[] => {
  const productsCopy = [...products];
  const unwrappedProducts = productsCopy.map((product) => {
    if (!product) {
      return;
    }

    const colors = product.Variants.map((variant) => variant.Color).filter(
      (color) => !!color,
    );

    const colorsGroupedAcrossVariants = colors.reduce((group: any, color) => {
      group[color.Color] = color;
      return group;
    }, {});
    const uniqueColorsAcrossVariants: ColorDto[] = Object.keys(
      colorsGroupedAcrossVariants,
    ).map((key) => colorsGroupedAcrossVariants[key]);

    // prettier-ignore
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { Variants, Specifications, ShortDescription, Seo, ...slimmedProduct } = product;

    const plainPrice = product?.Variants[0]?.Prices?.Price;

    if (plainPrice) {
      const firstVariant = product.Variants[0];
      const variantDefaultBundlePriceId = firstVariant.Prices.BundlePrices
        ? firstVariant.Prices.BundlePrices[0].BundlePriceId
        : undefined;
      const cardArray = [
        {
          key: useId(),
          product: slimmedProduct,
          variant: firstVariant,
          colors: uniqueColorsAcrossVariants,
          variantDefaultBundlePriceId,
        } as ProductCard,
      ];

      // Exclude nested large data not necessary in product lists
      cardArray[0].variant.Prices.BundlePrices = undefined;

      return cardArray;
    }

    // Only add one variant per condition
    const variantsToAdd: VariantDto[] = [];
    const conditionsAccountedFor: { [key: string]: true } = {};
    for (const variant of product.Variants) {
      if (
        conditionsAccountedFor[variant.Condition] ||
        variant.Prices === null
      ) {
        continue;
      }

      variantsToAdd.push(variant);
      conditionsAccountedFor[variant.Condition] = true;
    }

    return (
      variantsToAdd.map((variant) => {
        const variantDefaultBundlePriceId = variant?.Prices?.BundlePrices
          ? variant.Prices.BundlePrices[0].BundlePriceId
          : undefined;

        return (
          variant.Prices?.BundlePrices?.map((bundlePrice) => {
            return (
              bundlePrice.Payments.map((payment) => {
                const productCard: ProductCard = {
                  key: useId(),
                  product: slimmedProduct,
                  bundlePrice,
                  variantDefaultBundlePriceId,
                  payment,
                  variant,
                  colors: uniqueColorsAcrossVariants,
                };

                // Exclude nested large data not necessary in product lists
                productCard.variant = {
                  ...productCard.variant,
                  Prices: {
                    ...productCard.variant.Prices,
                    BundlePrices: undefined,
                  },
                };

                return productCard;
              }) || []
            );
          }) || []
        );
      }) || []
    );
  });

  const flatProducts = unwrappedProducts.flatMap(
    (perVariant) =>
      perVariant?.flatMap((perBundle) =>
        Array.isArray(perBundle)
          ? perBundle.flatMap((perPayment) => perPayment || [])
          : perBundle,
      ) || [],
  );

  const withoutFamilyPlans =
    flatProducts?.filter((card) => !card?.bundlePrice?.Plan?.IsFamilyPlan) ||
    [];

  return withoutFamilyPlans;
};
